import { useEffect, useRef, useState } from 'react';

import awsmobile from '../config/aws-exports';
import apiRequest from '../utils/apiRequest';
import { getDomainPrefix } from '../utils/branding';
import ensureError from '../utils/ensureError';

type UseInstallerBrandArgs = {
  onError?: (e: unknown) => void;
};

type UseInstallerBrandResult = {
  domainPrefix: string;
  installerId: string;
  brandResolving: boolean;
};

interface InstallerBrand {
  installer_id: string;
  name?: string;
  logo_url?: string;
}

const safeReportError = (handler: ((e: unknown) => void) | undefined, err: unknown): void => {
  try {
    if (typeof handler === 'function') handler(ensureError(err));
  } catch {}
};

const devHint = (): string =>
  process.env.NODE_ENV !== 'production' ? ' In development, use a branded host like loop-optimise.localhost:3000.' : '';

/**
 * Hook to resolve domainPrefix and installerId from the current hostname.
 */
export function useInstallerBrand({ onError }: UseInstallerBrandArgs): UseInstallerBrandResult {
  const [domainPrefix, setDomainPrefix] = useState<string>('');
  const [installerId, setInstallerId] = useState<string>('');
  const [brandResolving, setBrandResolving] = useState<boolean>(true);

  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const init = async () => {
      setBrandResolving(true);
      const prefix = getDomainPrefix();
      if (mountedRef.current) setDomainPrefix(prefix);

      if (!prefix) {
        if (mountedRef.current) setBrandResolving(false);
        try {
          if (typeof onError === 'function') {
            onError(new Error('Missing domain prefix.' + devHint()));
          }
        } catch {}
        return;
      }

      try {
        // TODO: Migrate request to a method in an api client file
        const brand = await apiRequest<InstallerBrand>(
          `${awsmobile.optimise_endpoint_auth}/installer/brand?domain_prefix=${encodeURIComponent(prefix)}`,
          null,
          'GET',
          undefined,
          { auth: false, signal: controller.signal }
        );
        const safeBrand = brand || {};
        const id = safeBrand?.installer_id || '';
        if (!id) throw new Error('Missing installer_id for this brand');
        if (mountedRef.current) setInstallerId(id);
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === 'AbortError') return;
        safeReportError(onError, e);
      } finally {
        if (mountedRef.current) setBrandResolving(false);
      }
    };
    void init();
    return () => {
      controller.abort();
    };
  }, [onError]);

  return { domainPrefix, installerId, brandResolving };
}

export default useInstallerBrand;
